import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { TimeUtils } from '@freelancer/time-utils';
import { Assets } from '@freelancer/ui/assets';
import { PictureLoadMethod, PictureObjectFit } from '@freelancer/ui/picture';
import type { HeroData } from 'app/home/hero/home-page-hero.types';
import type { Observable } from 'rxjs';
import { Subscription, of } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-hero-image-carousel',
  template: `
    @for (hero of heroes; track trackById(i, hero); let i = $index) {
      @if (startPlaying || i === 0) {
        <fl-picture
          i18n-alt="ImageLoader"
          alt="ImageLoader"
          class="BackgroundImage"
          [class.IsShown]="i === currentIndex"
          [srcLargeDesktop]="hero.desktopLargeImage"
          [srcDesktop]="hero.desktopSmallImage"
          [srcTablet]="hero.tabletImage"
          [src]="hero.mobileImage"
          [loadMethod]="i === 0 ? PictureLoadMethod.PRELOAD : undefined"
          [objectFit]="PictureObjectFit.COVER"
          [fullWidth]="true"
        ></fl-picture>
      }
    }
  `,
  styleUrls: ['./image-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroImageCarouselComponent
  implements OnInit, OnChanges, OnDestroy
{
  private assets = inject(Assets);
  private timeUtils = inject(TimeUtils);

  PictureLoadMethod = PictureLoadMethod;
  PictureObjectFit = PictureObjectFit;

  @Input() heroes: readonly HeroData[];

  @Input() startPlaying: boolean;

  @Output()
  indexChanged = new EventEmitter<number>();

  timer$: Observable<number>;
  @RepetitiveSubscription()
  private currentIndexSubscription?: Subscription;
  currentIndex = 0;
  nextIndex = 0;

  ngOnInit(): void {
    this.timer$ = of(0);

    this.heroes = this.heroes.map(hero => ({
      ...hero,
      mobileImage: this.assets.getUrl(hero.mobileImage),
      tabletImage: this.assets.getUrl(hero.tabletImage),
      desktopSmallImage: this.assets.getUrl(hero.desktopSmallImage),
      desktopLargeImage: this.assets.getUrl(hero.desktopLargeImage),
    }));

    if (this.startPlaying) {
      this.startTimer();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('startPlaying' in changes) {
      if (this.startPlaying) {
        this.startTimer();
      }
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeToCurrentIndex();
  }

  trackById(index: number, value: HeroData): number {
    return value.id;
  }

  startTimer(): void {
    this.unsubscribeToCurrentIndex();
    this.timer$ = this.timeUtils.rxInterval(5000);
    this.subscribeToCurrentIndex();
  }

  subscribeToCurrentIndex(): void {
    this.currentIndexSubscription = this.timer$.subscribe(index => {
      this.currentIndex = index % this.heroes.length;
      if (this.nextIndex < 3) {
        this.nextIndex = this.currentIndex + 1;
      }
      this.indexChanged.emit(this.currentIndex);
    });
  }

  unsubscribeToCurrentIndex(): void {
    if (this.currentIndexSubscription) {
      this.currentIndexSubscription.unsubscribe();
    }
  }
}
